/**
 * The panel's charts. Hand-rolled SVG, because this app has no chart library
 * and a 200 KB dependency to draw a 40-point sparkline would be the wrong trade
 * on a console an operator keeps open all day.
 *
 * ============================================================================
 * COLOUR RULES, which are not stylistic here.
 * ============================================================================
 *
 * `ok` / `warn` / `crit` / `offline` are STATUS colours and are reserved. They
 * mean a machine's condition, everywhere in this panel, and a chart that spent
 * them on "series 2" would teach an operator that green means a series rather
 * than that green means healthy — on the one screen where that confusion has a
 * cost. So series wear `brand` and `gold`; status marks wear status colours and
 * always carry a label beside them, never colour alone.
 *
 * Every colour arrives as `currentColor` through a Tailwind text class, so both
 * themes work without a single hex in this file (which `mbt:check` enforces
 * anyway).
 */
import { t } from '../i18n/index.js';

/** The shape of one sparkline: values oldest → newest. */
export interface SeriesProps {
  readonly values: readonly number[];
  readonly className?: string;
  /** Drawn as a dotted rule across the plot — a target, a cap, an average. */
  readonly reference?: number | null;
}

/**
 * A sparkline: shape over time, no axes, no numbers on points.
 *
 * §15.9's instrument reading. It answers "is this rising, falling or flat"
 * at a glance and deliberately answers nothing else — the exact value belongs
 * to the `Figure` beside it, and a number on every point is the anti-pattern
 * that makes a sparkline unreadable at this size.
 */
export function Sparkline({ values, className = 'text-brand', reference = null }: SeriesProps) {
  if (values.length < 2) {
    /*
      A FLAT RULE, not an empty box. One reading (or none) is a real state — a
      kiosk that has reported once — and an empty div of the same height reads
      as a chart that failed to render. The rule says "there is nothing to plot
      yet" in the same space the plot will occupy, so nothing jumps when the
      second point arrives.
    */
    return (
      <svg viewBox="0 0 100 28" preserveAspectRatio="none" className={`h-10 w-full ${className}`} aria-hidden="true">
        <line x1="0" x2="100" y1="26" y2="26" stroke="currentColor" strokeWidth="1" opacity="0.25" />
      </svg>
    );
  }
  const width = 100;
  const height = 28;
  const max = Math.max(...values, reference ?? Number.NEGATIVE_INFINITY);
  const min = Math.min(...values, reference ?? Number.POSITIVE_INFINITY);
  // A flat series must not divide by zero, and must draw down the MIDDLE rather
  // than pinned to the floor — a flat line at the bottom reads as "zero" when
  // it means "unchanged".
  const span = max - min || 1;
  const x = (index: number) => (index / (values.length - 1)) * width;
  const y = (value: number) => height - ((value - min) / span) * (height - 4) - 2;

  const line = values.map((value, index) => `${x(index)},${y(value)}`).join(' ');
  const area = `${x(0)},${height} ${line} ${x(values.length - 1)},${height}`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={`h-10 w-full ${className}`}
      role="img"
      aria-label={t('chart.trend')}
    >
      <polygon points={area} fill="currentColor" opacity="0.12" />
      {reference === null ? null : (
        <line
          x1="0"
          x2={width}
          y1={y(reference)}
          y2={y(reference)}
          stroke="currentColor"
          strokeWidth="0.5"
          strokeDasharray="3 3"
          opacity="0.5"
        />
      )}
      <polyline
        points={line}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        /* Drawn left to right, in the direction time runs. `pathLength` fixes
           the dash arithmetic at 1000 regardless of the real geometry, so one
           keyframe works for every series length. */
        pathLength={1000}
        strokeDasharray="1000"
        className="animate-draw"
      />
      {/* The newest point, marked. A sparkline without it makes the reader hunt
          for which end is now. */}
      <circle cx={x(values.length - 1)} cy={y(values[values.length - 1] ?? 0)} r="1.8" fill="currentColor" />
    </svg>
  );
}

export interface DonutSlice {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  /** A Tailwind text class — status colours are legal HERE, with the label. */
  readonly className: string;
}

/**
 * The status ring, with its legend beside it — never without.
 *
 * A donut is a weak form for comparing magnitudes and a strong one for "how is
 * the fleet split, right now", which is the question §15.7 puts at the top of
 * the executive home. The centre carries the headline count so the ring is
 * decoration around a number rather than the only way to read it.
 */
export function StatusDonut({
  slices,
  centreValue,
  centreLabel,
}: {
  readonly slices: readonly DonutSlice[];
  readonly centreValue: string;
  readonly centreLabel: string;
}) {
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="flex items-center gap-6">
      <svg viewBox="0 0 100 100" className="h-32 w-32 shrink-0 -rotate-90" role="img" aria-label={centreLabel}>
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          className="text-hairline"
          stroke="currentColor"
          strokeWidth="10"
        />
        {total === 0
          ? null
          : slices.map((slice, index) => {
              const length = (slice.value / total) * circumference;
              const dash = `${Math.max(0, length - 2)} ${circumference - Math.max(0, length - 2)}`;
              const element = (
                <circle
                  key={slice.key}
                  cx="50"
                  cy="50"
                  r={radius}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="10"
                  strokeDasharray={dash}
                  strokeDashoffset={-offset}
                  strokeLinecap="butt"
                  className={`${slice.className} animate-sweep`}
                  style={{ animationDelay: `${index * 90}ms` }}
                />
              );
              offset += length;
              return element;
            })}
      </svg>
      <div className="min-w-0 flex-1">
        <p className="text-hero font-semibold leading-none text-ink-100">{centreValue}</p>
        <p className="mt-1 text-micro uppercase tracking-wide text-ink-500">{centreLabel}</p>
        {/* The legend is always present. Identity is never colour alone — that
            is the accessibility rule and also just the readable choice on a
            wall-mounted screen somebody glances at from across a room. */}
        <ul className="mt-3 space-y-1">
          {slices.map((slice) => (
            <li key={slice.key} className="flex items-center gap-2 text-micro">
              <span className={`h-2 w-2 shrink-0 rounded-full ${slice.className}`} style={{ backgroundColor: 'currentColor' }} />
              <span className="flex-1 truncate text-ink-300">{slice.label}</span>
              <span className="tabular-nums text-ink-100">{slice.value}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * A horizontal bar row — the right form for "which mahalla filed the most",
 * where the labels are words of very different lengths and a vertical axis
 * would rotate them into unreadability.
 */
export function BarRow({
  label,
  value,
  max,
  display,
  className = 'text-brand',
}: {
  readonly label: string;
  readonly value: number;
  readonly max: number;
  readonly display: string;
  readonly className?: string;
}) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      {/* 224px, not 128. These labels are whole Uzbek phrases — «Dastur
          qayta-qayta ishga tushmoqda» — and a rule the reader cannot name is a
          rule they cannot act on. `title` carries the full text for the rare
          one that still runs long. */}
      <span className="w-56 shrink-0 truncate text-micro text-ink-300" title={label}>
        {label}
      </span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-hairline">
        {/* Rounded data-end anchored to the baseline; 2px surface gap comes from
            the track's own background showing through. */}
        <div className={`h-full rounded-full ${className}`} style={{ width: `${pct}%`, backgroundColor: 'currentColor' }} />
      </div>
      <span className="w-14 shrink-0 text-right text-micro tabular-nums text-ink-100">{display}</span>
    </div>
  );
}

/**
 * A labelled meter — CPU, disk, load. The bar is the glance; the number is the
 * answer. Tone is a STATUS judgement and therefore wears a status colour, with
 * the number always beside it so the colour is never the only signal.
 */
export function Meter({
  label,
  pct,
  display,
  tone = 'neutral',
}: {
  readonly label: string;
  readonly pct: number | null;
  readonly display: string;
  readonly tone?: 'neutral' | 'ok' | 'warn' | 'crit';
}) {
  const toneClass =
    tone === 'crit'
      ? 'text-crit'
      : tone === 'warn'
        ? 'text-warn'
        : tone === 'ok'
          ? 'text-ok'
          : 'text-brand';
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-micro uppercase tracking-wide text-ink-500">{label}</span>
        <span className="text-micro tabular-nums text-ink-100">{display}</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-hairline">
        <div
          className={`h-full rounded-full ${toneClass}`}
          style={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%`, backgroundColor: 'currentColor' }}
        />
      </div>
    </div>
  );
}

/**
 * Two series over time, with a shared axis — received against closed.
 *
 * ============================================================================
 * ONE AXIS. NEVER TWO. This is the chart mistake that matters most.
 * ============================================================================
 *
 * Received and closed are the same unit (appeals) so they share a scale, and
 * the gap between the lines is the whole story: it is the backlog growing or
 * shrinking. Put them on two scales and that gap becomes an artefact of the
 * axis ranges — the lines can be made to cross, or not, by choosing bounds, and
 * a governor would be reading a decision out of a rendering choice.
 *
 * Both series wear SERIES colours (brand, gold) rather than ok/crit. Neither
 * "received" nor "closed" is good or bad on its own; painting one green would
 * be the chart taking a position the data does not support.
 */
export function DualSeries({
  points,
  labelA,
  labelB,
  format,
}: {
  readonly points: readonly { t: string; a: number; b: number }[];
  readonly labelA: string;
  readonly labelB: string;
  readonly format: (iso: string) => string;
}): React.JSX.Element {
  if (points.length < 2) {
    return <p className="py-8 text-center text-label text-ink-500">{t('app.notYet')}</p>;
  }
  const width = 100;
  const height = 30;
  const max = Math.max(...points.flatMap((p) => [p.a, p.b]), 1);
  const x = (i: number) => (i / (points.length - 1)) * width;
  const y = (v: number) => height - (v / max) * (height - 2) - 1;
  const line = (pick: (p: { a: number; b: number }) => number) =>
    points.map((p, i) => `${x(i)},${y(pick(p))}`).join(' ');

  const first = points[0];
  const last = points[points.length - 1];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4">
        {/* A legend for two series is mandatory, and both are also named on the
            axis line below — identity is never colour alone. */}
        <span className="flex items-center gap-1.5 text-micro text-ink-300">
          <span className="h-0.5 w-4 bg-brand" />
          {labelA}
        </span>
        <span className="flex items-center gap-1.5 text-micro text-ink-300">
          <span className="h-0.5 w-4 bg-gold" />
          {labelB}
        </span>
        <span className="ml-auto text-micro tabular-nums text-ink-500">
          {t('chart.peak')}: {max}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="mt-2 h-44 w-full"
        role="img"
        aria-label={`${labelA} / ${labelB}`}
      >
        {/* Three recessive gridlines. Enough to judge a level, few enough that
            the data stays the loudest thing in the frame. */}
        {[0.25, 0.5, 0.75].map((fraction) => (
          <line
            key={fraction}
            x1="0"
            x2={width}
            y1={height * fraction}
            y2={height * fraction}
            stroke="currentColor"
            strokeWidth="0.2"
            className="text-hairline"
          />
        ))}
        {/* Staggered by 120 ms so the two lines resolve as two lines rather
            than one thick one — the gap between them is the whole message. */}
        <polyline
          points={line((p) => p.a)}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          className="text-brand animate-draw"
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
          pathLength={1000}
          strokeDasharray="1000"
        />
        <polyline
          points={line((p) => p.b)}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          className="text-gold animate-draw"
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
          pathLength={1000}
          strokeDasharray="1000"
          style={{ animationDelay: '120ms' }}
        />
      </svg>
      {/* Only the ends are labelled. A tick per day on 30+ buckets is a smear,
          and the question this chart answers is about shape, not about the
          value on the 14th. */}
      <div className="flex justify-between text-micro tabular-nums text-ink-500">
        <span>{first ? format(first.t) : ''}</span>
        <span>{last ? format(last.t) : ''}</span>
      </div>
    </div>
  );
}

/**
 * A radial gauge — one percentage, read from across a room.
 *
 * ============================================================================
 * WHY A GAUGE AND NOT A NUMBER, WHEN A NUMBER IS SMALLER AND EXACTER.
 * ============================================================================
 *
 * Because this form answers "how close to the line" without arithmetic. «94%»
 * against a 95% statutory target is a comparison the reader has to perform;
 * an arc that visibly stops short of its mark is one they perform by looking.
 * The number is still there in the middle — the gauge is the glance, the digits
 * are the record.
 *
 * The TARGET IS DRAWN, always. A gauge with no mark is a decorative ring: 94%
 * of an unstated target means nothing, and every figure on this screen that has
 * a legal threshold should show it.
 */
export function Gauge({
  pct,
  target = null,
  label,
  tone = 'text-brand',
}: {
  readonly pct: number | null;
  readonly target?: number | null;
  readonly label: string;
  readonly tone?: string;
}) {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  /* Three quarters of the circle, opening at the bottom — the dial convention,
     and it leaves room under the arc for the label without crowding it. */
  const span = 0.75;
  const value = pct === null ? 0 : Math.max(0, Math.min(100, pct));
  const filled = circumference * span * (value / 100);

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 100 100" className="h-28 w-28" role="img" aria-label={label}>
        <g transform="rotate(135 50 50)">
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            strokeLinecap="round"
            className="text-hairline"
            strokeDasharray={`${circumference * span} ${circumference}`}
          />
          {pct === null ? null : (
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth="8"
              strokeLinecap="round"
              className={`${tone} animate-sweep`}
              strokeDasharray={`${filled} ${circumference}`}
            />
          )}
          {target === null ? null : (
            /* The mark. A 2-unit tick in ink, not in the series colour, so it
               reads as a rule rather than as more of the measurement. */
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth="8"
              className="text-ink-100"
              strokeDasharray={`1.5 ${circumference}`}
              strokeDashoffset={-circumference * span * (target / 100)}
            />
          )}
        </g>
      </svg>
      <p className="-mt-16 text-figure font-bold tabular-nums text-ink-100">
        {pct === null ? '—' : `${Math.round(pct)}%`}
      </p>
      <p className="mt-6 text-micro uppercase tracking-wide text-ink-500">{label}</p>
    </div>
  );
}

/**
 * Vertical bars that grow from the baseline, with their values above them.
 *
 * The house console's monthly chart. Values are printed rather than left to an
 * axis because there are few enough bars to label them all, and a labelled bar
 * needs no gridlines — which removes the furniture and leaves the data.
 */
export function BarChart({
  bars,
  tone = 'text-brand',
}: {
  readonly bars: readonly { key: string; label: string; value: number; highlight?: boolean }[];
  readonly tone?: string;
}) {
  /*
    NO BARS *AND* ALL-ZERO BARS BOTH GET THE CAPTION, NOT A PLOT.

    The all-zero case used to draw the frame: 160 px of chart with a 3 px navy
    sliver under each label. On the console at 08:00, with no appeals taken
    yet, that is a large empty rectangle that reads as a chart which failed to
    load — and the one thing it does not say is «nobody has used a kiosk
    today», which is the actual fact and is worth one line of text.

    A form is only a chart when there is something to compare.
  */
  if (bars.length === 0 || bars.every((bar) => bar.value === 0)) {
    return <p className="py-8 text-center text-label text-ink-500">{t('app.notYet')}</p>;
  }
  const max = Math.max(...bars.map((bar) => bar.value), 1);
  return (
    /*
      ========================================================================
      THE GEOMETRY IS THE WHOLE COMMENT. A PERCENTAGE HEIGHT NEEDS A DEFINITE
      PARENT, AND THIS CHART DID NOT HAVE ONE.
      ========================================================================

      The first version put value, bar and label in one column and set the
      bar's height in percent. The row above carried `items-end`, which
      replaces the default `stretch` — so each column's height became its
      CONTENT height, the percentage had nothing to resolve against, and every
      bar collapsed to nothing. A remediation chart with a single value of 3
      rendered as an empty white panel with a «3» under it.

      (This is the second time in this file: `PipelineStage` lost its bars to
      exactly the same cause. Percentages resolve against definite heights, and
      a flex column only has one if something gives it one.)

      So the plot area is now its own box: `flex-1` inside a column of definite
      height IS definite, which is what the bar's percentage needs. The value
      sits above that box and the labels in a separate row below, so neither
      steals height from the plot and a 100%-tall bar cannot push its own label
      off the bottom.
    */
    <div>
      {/* `justify-center`: four bars sit as a group under the panel rather than
          hugging the left edge, and twelve fill it — `flex-1` up to the cap. */}
      <div className="flex h-40 justify-center gap-2">
        {bars.map((bar, index) => (
          <div key={bar.key} className="flex h-full min-w-0 max-w-bar flex-1 flex-col gap-1">
            <span className="shrink-0 text-center text-micro font-semibold tabular-nums text-ink-300">
              {bar.value}
            </span>
            <div className="flex min-h-0 flex-1 items-end">
              <div
                className={`w-full origin-bottom rounded-t animate-rise ${
                  bar.highlight ? 'text-gold' : tone
                }`}
                style={{
                  /* A floor of 3%, so a bar of zero beside a bar of forty is a
                     visible tick rather than an absence — «this kiosk took no
                     appeals» is data, and it must not render as «this kiosk is
                     not in the chart». */
                  height: `${Math.max(3, (bar.value / max) * 100)}%`,
                  backgroundColor: 'currentColor',
                  /* Staggered left to right: the eye follows the build in
                     reading order, which makes a twelve-bar chart resolve as a
                     sequence rather than as everything arriving at once. */
                  animationDelay: `${index * 45}ms`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
      {/* The label row mirrors the plot row's widths and alignment EXACTLY, or
          the captions stop sitting under their bars. */}
      <div className="mt-1 flex justify-center gap-2">
        {bars.map((bar) => (
          <span
            key={bar.key}
            className="min-w-0 max-w-bar flex-1 truncate text-center text-micro text-ink-500"
            title={bar.label}
          >
            {bar.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * A DISTRIBUTION STRIP — one tick per member, tallest to shortest.
 *
 * ============================================================================
 * WHY THIS EXISTS AND IS NOT A SPARKLINE.
 * ============================================================================
 *
 * A sparkline is a statement about TIME. Its line says «this followed that»,
 * and a reader takes the left-hand end as older whether or not anyone told
 * them so — that is the whole reason the form is legible without an axis.
 *
 * Several KPI cards here were feeding it a value PER KIOSK: disk-free across
 * four machines, plays across twelve campaigns. There is no time in that, and
 * the order is whatever the API returned, so the line's shape was an artefact
 * of a sort order. It read as a trend and was not one.
 *
 * The honest form for «how is this spread across the fleet» is a ranked strip:
 * sorted descending, so the shape carries the only meaning it can — a flat
 * strip means everything is alike, a cliff means one member is the outlier.
 * Nobody reads left-to-right as time in a bar strip.
 */
export function DistributionStrip({
  values,
  className = 'text-brand',
}: {
  readonly values: readonly number[];
  readonly className?: string;
}) {
  const ranked = [...values].sort((a, b) => b - a);
  const max = Math.max(...ranked, 1);
  /*
    FEWER THAN TWO MEMBERS IS NOT A DISTRIBUTION — render nothing.

    With one value the tallest bar is also the only bar, so it is drawn at 100%
    and fills the box: a solid rectangle. On the console's «lowest disk» card,
    where one kiosk was online reporting 75%, that solid block read as a
    progress bar at 100% — the opposite of the number beside it.

    A spread needs something to spread across. The card still has its figure.
  */
  if (ranked.length < 2) {
    return <div className="h-8" />;
  }
  return (
    <div className="flex h-8 items-end gap-px" aria-hidden="true">
      {ranked.map((value, index) => (
        <div
          key={index}
          className={`min-w-px flex-1 rounded-t ${className}`}
          style={{
            /* A floor of 8% so a zero member is still a visible tick — «this
               kiosk reported nothing» and «this kiosk is not in the list» are
               different facts and must not render identically. */
            height: `${Math.max(8, (value / max) * 100)}%`,
            backgroundColor: 'currentColor',
            opacity: value === 0 ? 0.25 : 1,
          }}
        />
      ))}
    </div>
  );
}

/**
 * A day × hour heat grid.
 *
 * The one form that answers "WHEN do citizens actually come to the kiosks",
 * which a line chart cannot: a daily total hides that everybody arrives between
 * nine and eleven, and that is the fact a staffing decision turns on.
 *
 * Intensity is one hue at varying alpha — never a rainbow. A sequential measure
 * gets a sequential ramp, and a viewer should be able to rank two cells by
 * looking without consulting a legend of five colours.
 */
export function HeatGrid({
  cells,
  rowLabels,
  colLabels,
  max,
}: {
  readonly cells: readonly (number | null)[][];
  readonly rowLabels: readonly string[];
  readonly colLabels: readonly string[];
  readonly max: number;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="border-separate border-spacing-0.5">
        <tbody>
          {cells.map((row, r) => (
            <tr key={rowLabels[r] ?? r}>
              <th scope="row" className="pr-2 text-right text-micro font-normal text-ink-500">
                {rowLabels[r]}
              </th>
              {row.map((value, c) => (
                <td key={colLabels[c] ?? c} className="p-0">
                  <div
                    className="h-5 w-5 rounded-sm bg-brand"
                    style={{
                      /* A floor of 0.06 so an empty cell is still a cell — a
                         fully transparent square reads as missing data rather
                         than as a quiet hour. */
                      opacity: value === null ? 0.06 : 0.06 + (value / (max || 1)) * 0.94,
                    }}
                    title={`${rowLabels[r] ?? ''} ${colLabels[c] ?? ''}: ${value ?? 0}`}
                  />
                </td>
              ))}
            </tr>
          ))}
          <tr>
            <td />
            {colLabels.map((label) => (
              <td key={label} className="pt-1 text-center text-micro text-ink-500">
                {label}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
